import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'

import placeholderImage from 'components/AsyncProfilePicture/placeholder.jpg'
import Heading from 'components/Heading/Heading'
import ImgixImage from 'components/ImgixImage/ImgixImage'
import PotentialProfileTag from 'components/PotentialProfileTag/PotentialProfileTag'
import ProfileCardBadgeWrapper from 'components/ProfileCardBadgeWrapper/ProfileCardBadgeWrapper'
import { AlgoliaProfileHitProps } from 'components/SearchResults/AlgoliaProfileHitProps'
import Price from 'components/SearchResults/Price'
import useWriteSearchHitToCache from 'components/SearchResults/useWriteSearchHitToCache'
import useEffectOnce from 'hooks/useEffectOnce'
import usePushImpression from 'hooks/useImpressions'
import useIntersectionObserver from 'hooks/useIntersectionObserver'

import styles from './ResponsiveProfileCard.module.css'

interface ResponsiveProfileCardContentProps {
  name: string
  title?: string
  image?: {
    url?: string
    id?: string
  }
  price?: string
  discount?: string
  // eslint-disable-next-line react/no-unused-prop-types
  isPotentialProfile: boolean
  badge: {
    tagTitle: string
    badgePosition?: string
    color?: string
    backgroundColor?: string
  }
  setRef: (ref: React.MutableRefObject<HTMLDivElement>) => void
}

interface ResponsiveProfileCardProps {
  hit: AlgoliaProfileHitProps
  potentialProfile: boolean
}

interface ProfilePictureProps {
  url?: string
  alt?: string
  roundedTop?: boolean
}
const ProfilePicture: React.FC<ProfilePictureProps> = ({
  url,
  alt = '',
  roundedTop = false,
}) => {
  return (
    <div
      className={cn(styles.imageWrapper, {
        [styles.imageRoundedTop]: roundedTop,
      })}
    >
      {url ? (
        <ImgixImage src={url} alt={alt} layout="fill" />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={placeholderImage} alt={alt} width={225} height={300} />
      )}
    </div>
  )
}

const ProfileCard = (props: ResponsiveProfileCardContentProps) => {
  const {
    name,
    title,
    image,
    price,
    discount,
    isPotentialProfile,
    badge,
    setRef,
  } = props
  const ref = useRef<HTMLDivElement>()

  useEffect(() => {
    if (ref && ref.current) setRef(ref)
  }, [ref, setRef])

  return (
    <div ref={ref} className={styles.profileCard}>
      <div className={styles.media}>
        <ProfilePicture url={image?.url} alt={name} roundedTop />
        {!!badge && <ProfileCardBadgeWrapper badges={[badge]} />}
      </div>
      <div className={styles.aspect} />
      <main className={styles.content}>
        <div className={styles.priceAndPlayWrapper}>
          {price && <Price amount={price} discount={discount} />}
          {isPotentialProfile && (
            <PotentialProfileTag profileName={name} style={{}} />
          )}
        </div>
        <div className={cn(styles.details)}>
          <Heading level="h5">
            <h4>{name}</h4>
          </Heading>
          <div className={styles.title}>{title}</div>
        </div>
      </main>
    </div>
  )
}

const ResponsiveProfileCard = (
  props: ResponsiveProfileCardProps,
): JSX.Element => {
  const { hit, potentialProfile } = props
  const { t } = useTranslation()
  const { writeSearchHitToCache } = useWriteSearchHitToCache()
  const isVisible = useIntersectionObserver()
  const pushImpression = usePushImpression()

  useEffectOnce(() => {
    if (!potentialProfile) {
      writeSearchHitToCache(hit)
    }
  })

  const [profileRef, setProfileRef] =
    useState<React.MutableRefObject<HTMLDivElement>>(null)

  pushImpression(isVisible(profileRef, 0.7), {
    id: hit.objectID,
    name: hit.profileName,
    price: hit.price.priceInCents / 100,
    category: null,
    list: 'search',
    position: hit.__position,
    productId: hit.productId,
    profileId: hit.profileId,
    profileSlug: hit.slug,
    productSlug: '',
  })

  const notAvailableBadge = {
    badgeType: 'TEXT_BADGE',
    tagTitle: t(
      'SearchResultProfileCard.notAvailableBadgeText',
      'Not available',
    ),
    backgroundColor: '#222',
  }

  const badge =
    hit.profileAcceptingOrders === false ||
    hit.profileProductAcceptingOrders === false
      ? notAvailableBadge
      : hit.profileBadgeContent

  return (
    <ProfileCard
      name={hit.profileName}
      title={hit.profileTitle}
      image={{ url: hit.profilePictureUrl, id: hit.profilePictureId }}
      price={hit.price?.formattedPrice}
      discount={hit.price?.formattedDiscountedPrice}
      isPotentialProfile={false}
      badge={badge}
      setRef={setProfileRef}
    />
  )
}

export default ResponsiveProfileCard
